import React, { useState } from 'react';
import { message } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '../../../system/client/src/services/api';

const initial = {
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
};

const SignupPage = () => {
    const { login } = useAuth();
    const navigate = useNavigate();

    const [values, setValues] = useState(initial);
    const [loading, setLoading] = useState(false);

    const set = (key) => (e) =>
        setValues((v) => ({ ...v, [key]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();

        // The form is noValidate, so the browser never enforces `required` —
        // every field the API insists on has to be checked here as well.
        if (!values.companyName.trim()) {
            message.error('Please enter your company name.');
            return;
        }
        // Phone is optional (John, 2026-10-02). When given, the same loose check
        // as the API (system/server/lib/phone.js): 7–15 digits, spaces and
        // + ( ) - . allowed.
        const phone = values.phone.trim();
        const phoneDigits = phone.replace(/\D/g, '').length;
        if (phone && (!/^\+?[\d\s().-]+$/.test(phone) || phoneDigits < 7 || phoneDigits > 15)) {
            message.error('Please enter a valid phone number.');
            return;
        }
        if (values.password !== values.confirmPassword) {
            message.error('Passwords do not match.');
            return;
        }
        if (values.password.length < 6) {
            message.error('Password must be at least 6 characters.');
            return;
        }

        setLoading(true);
        try {
            await api.post('auth/signup', {
                email: values.email,
                password: values.password,
                firstName: values.firstName,
                lastName: values.lastName,
                companyName: values.companyName.trim(),
                phone: phone || undefined,
            });

            await login(values.email, values.password);
            message.success('Account created.');
            navigate('/traders');
        } catch (error) {
            message.error(
                error.response?.data?.message || error.message || 'Could not create account.'
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="fs-auth-card wide">
            <div className="fs-auth-head">
                <span className="kicker">Create account</span>
                <h1>Get started.</h1>
                <p>Tell us a little about you and your company.</p>
            </div>

            <form onSubmit={onSubmit} noValidate>
                <div className="fs-auth-row">
                    <div className="form-field">
                        <label htmlFor="firstName">First name</label>
                        <input
                            id="firstName"
                            type="text"
                            autoComplete="given-name"
                            required
                            value={values.firstName}
                            onChange={set('firstName')}
                        />
                    </div>
                    <div className="form-field">
                        <label htmlFor="lastName">Last name</label>
                        <input
                            id="lastName"
                            type="text"
                            autoComplete="family-name"
                            required
                            value={values.lastName}
                            onChange={set('lastName')}
                        />
                    </div>
                </div>

                <div className="form-field">
                    <label htmlFor="companyName">Company</label>
                    <input
                        id="companyName"
                        type="text"
                        autoComplete="organization"
                        required
                        value={values.companyName}
                        onChange={set('companyName')}
                    />
                </div>

                <div className="form-field">
                    <label htmlFor="email">Work email</label>
                    <input
                        id="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={values.email}
                        onChange={set('email')}
                        placeholder="you@company.com"
                    />
                </div>

                <div className="form-field">
                    <label htmlFor="phone">Phone <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(optional)</span></label>
                    <input
                        id="phone"
                        type="tel"
                        autoComplete="tel"
                        maxLength={32}
                        value={values.phone}
                        onChange={set('phone')}
                        placeholder="07700 900123"
                    />
                </div>

                <div className="fs-auth-row">
                    <div className="form-field">
                        <label htmlFor="password">Password</label>
                        <input
                            id="password"
                            type="password"
                            autoComplete="new-password"
                            required
                            minLength={6}
                            value={values.password}
                            onChange={set('password')}
                        />
                    </div>
                    <div className="form-field">
                        <label htmlFor="confirmPassword">Confirm</label>
                        <input
                            id="confirmPassword"
                            type="password"
                            autoComplete="new-password"
                            required
                            value={values.confirmPassword}
                            onChange={set('confirmPassword')}
                        />
                    </div>
                </div>

                <div className="fs-auth-actions">
                    <button
                        type="submit"
                        className="btn btn-primary btn-block"
                        disabled={loading}
                    >
                        {loading ? 'Creating account…' : (
                            <>Create account <span className="arrow">→</span></>
                        )}
                    </button>
                </div>
            </form>

            <p className="fs-auth-switch">
                Already have an account? <Link to="/login">Sign in.</Link>
            </p>
        </section>
    );
};

export default SignupPage;
